import { Middleware } from '@reduxjs/toolkit';

import {
  fetchCertainPaymentMethodSuccess,
  fetchPaymentInformationSuccess,
  fillConfirmationForm,
  fillPaymentForm,
  processPaymentFailure,
  registerPaymentFailure,
  registerPaymentSuccess,
  setActiveScreen,
  updateConfirmationPayloadItem,
  updatePaymentFormPayloadItem,
} from '@cashier/cashier-data-access';
import {
  DynamicScreens,
  FormPayload,
  FormPayloadItem,
  PaymentActionError,
  ResultActionTypes,
  ValidationStatuses,
} from '@cashier/cashier-model';
import {
  eventBus,
  EventFactory,
  PaymentStateChecker,
  PreProcessorPayloadFilter,
} from '@cashier/cashier-shared';

export const platformProcessingInitiatorMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);

    const {
      cashierConfiguration: { paymentType },
      paymentProcessing: { result: paymentProcessingResult },
      paymentRegistration: { resultAction: paymentRegistrationResultAction },
    } = store.getState();

    if (action.type === fetchCertainPaymentMethodSuccess.toString()) {
      const { method } = action.payload;

      eventBus.emit(
        EventFactory.paymentFormReady(200, {
          redirectContainer: method.redirectContainer,
        }),
      );
    }

    if (
      [
        updatePaymentFormPayloadItem.toString(),
        fillPaymentForm.toString(),
      ].includes(action.type)
    ) {
      const {
        paymentMethod: {
          paymentMethod: { preprocessor },
        },
        paymentForm: { formPayload },
      } = store.getState();

      const filteredPayload = PreProcessorPayloadFilter.filter(
        formPayload,
        preprocessor,
      );

      const payloadItems = Object.entries(filteredPayload).map(
        ([name, payloadItem]: [string, FormPayloadItem]) => {
          return {
            name,
            value: payloadItem.value || null,
            isValid:
              payloadItem.validationStatus === ValidationStatuses.SUCCESS,
          };
        },
      );

      eventBus.emit(EventFactory.paymentAttributesChanged(payloadItems));
    }

    if (
      action.type === updateConfirmationPayloadItem.toString() ||
      action.type === fillConfirmationForm.toString()
    ) {
      const {
        confirmationForm: { formPayload },
      } = store.getState();

      eventBus.emit(
        EventFactory.paymentAttributesChanged(
          Object.entries(formPayload as FormPayload).map(
            ([name, payloadItem]: [string, FormPayloadItem]) => {
              return {
                name,
                value: payloadItem.value,
                isValid:
                  payloadItem.validationStatus === ValidationStatuses.SUCCESS,
              };
            },
          ),
        ),
      );
    }

    if (action.type === fetchPaymentInformationSuccess.toString()) {
      const resultAction = paymentProcessingResult.resultAction;
      if (
        resultAction?.type === ResultActionTypes.DISPLAY ||
        PaymentStateChecker.isSuccess(paymentType, action.payload.state)
      ) {
        eventBus.emit(EventFactory.paymentProcessed(200, {}));
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }

      if (PaymentStateChecker.isFailed(paymentType, action.payload.state)) {
        eventBus.emit(EventFactory.paymentProcessed(500, {}));
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }
    }

    if (action.type === processPaymentFailure.toString()) {
      const { context }: PaymentActionError = action.payload;
      eventBus.emit(EventFactory.paymentProcessed(500, {}));

      if (!context || context.ui.display === 'screen') {
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }
    }

    if (action.type === setActiveScreen.toString()) {
      if (action.payload === DynamicScreens.ConfirmationForm) {
        //@TODO: we do not need too much events here
        eventBus.emit(EventFactory.paymentProcessed(200, {}));
        eventBus.emit(EventFactory.paymentDataConfirmationInitiated());
      }

      if (
        action.payload === DynamicScreens.ProcessingResult ||
        action.payload === DynamicScreens.FinalScreen
      ) {
        eventBus.emit(EventFactory.paymentProcessingStarted());
      }
    }

    if (action.type === registerPaymentSuccess.toString()) {
      if (paymentRegistrationResultAction?.type === ResultActionTypes.DISPLAY) {
        eventBus.emit(EventFactory.paymentProcessed(200, {}));
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }
    }

    if (action.type === registerPaymentFailure.toString()) {
      const { context }: PaymentActionError = action.payload;
      eventBus.emit(EventFactory.paymentProcessed(500, {}));

      if (!context || context.ui.display === 'screen') {
        eventBus.emit(EventFactory.paymentAttributesChanged([]));
      }
    }

    return result;
  };
